import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Award, TrendingUp, DollarSign, Activity, Loader2, CheckCircle, Clock, FileCheck } from 'lucide-react';
import HomeworkReviewPanel from '@/components/HomeworkReviewPanel';

import { useContributorDashboard } from './useContributorDashboard';
import OverviewTab from './OverviewTab';
import ActivityTab from './ActivityTab';
import EarningsTab from './EarningsTab';

const ContributorDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const {
    loading,
    profile,
    points,
    payouts,
    refreshPoints,
  } = useContributorDashboard();
  
  // Loading state
  if (loading) {
    return (
      <Card className="mb-8">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-cyan-500" />
          <p className="mt-2 text-gray-500">Loading contributor data...</p>
        </CardContent>
      </Card>
    );
  }
  
  // Not a contributor
  if (!profile) {
    return null;
  }
  
  const threshold = profile?.eligibility_threshold || points?.eligibility_threshold || 100;
  
  return (
    <Card className="mb-8 border-2 border-cyan-500/30 bg-gradient-to-br from-slate-900/50 to-slate-800/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl text-white flex items-center gap-2">
              <Award className="w-6 h-6 text-cyan-400" />
              Contributor Dashboard
            </CardTitle>
            <CardDescription className="text-gray-400">
              Track your contributions and earnings
            </CardDescription>
          </div>
          <Badge className={points?.is_eligible ? 'bg-green-600' : 'bg-slate-600'}>
            {points?.is_eligible ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Eligible for Payout</>
            ) : (
              <><Clock className="w-3 h-3 mr-1" /> {points?.points_needed ?? threshold} pts to Eligibility</>
            )}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 mb-4 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-cyan-600">
              <TrendingUp className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="homework" className="data-[state=active]:bg-cyan-600">
              <FileCheck className="w-4 h-4 mr-2" />
              Homework Review
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-cyan-600">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="earnings" className="data-[state=active]:bg-cyan-600">
              <DollarSign className="w-4 h-4 mr-2" />
              Earnings
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <OverviewTab points={points} payouts={payouts} threshold={threshold} />
          </TabsContent>
          
          <TabsContent value="homework">
            <HomeworkReviewPanel user={user} onPointsEarned={refreshPoints} />
          </TabsContent>
          
          <TabsContent value="activity">
            <ActivityTab points={points} />
          </TabsContent>
          
          <TabsContent value="earnings">
            <EarningsTab payouts={payouts} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ContributorDashboard;
